import { Stack, Title, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  ErrorPage,
  LoadingPage,
  PageContainer,
  SimpleStepDots,
  StepProps,
} from '../../../shared_components';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance, loadStripe, Stripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { CheckoutForm } from './CheckoutForm';
import { OrderDataStoreContext } from '../stores/orderDataStore';
import { useStore } from '../../../shared_hooks';
import { observer } from 'mobx-react-lite';
import { useKeycloakStore } from '../../../shared_stores';

const stripeApiKey = process.env.NX_PUBLIC_STRIPE_API_KEY;

export const StepPay = observer(({ forward, back }: StepProps) => {
  const { t } = useTranslation(['payment', 'errors', 'common']);
  const orderDataStore = useStore(OrderDataStoreContext);
  const kcStore = useKeycloakStore();

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const clientSecret = orderDataStore.clientSecret;

  const stripeLoaded =
    stripePromise !== undefined && clientSecret !== undefined;

  const appearance: Appearance = {
    theme: 'stripe',

    labels: 'floating',

    variables: {
      borderRadius: '10px',
      colorPrimary: 'var(--mantine-primary-color-filled)',
      gridColumnSpacing: '8px',
      gridRowSpacing: '16px',
    },
  };

  useEffect(() => {
    if (stripeApiKey) {
      setStripePromise(
        loadStripe(stripeApiKey, {
          locale: 'de',
        }),
      );
    }
  }, []);

  useEffect(() => {
    const authToken = kcStore.authenticated ? kcStore.token : '';
    void orderDataStore.loadClientSecret(authToken);
  }, [orderDataStore, kcStore]);

  useEffect(() => {
    if (orderDataStore.deviceFairMode) forward();
  }, [orderDataStore.deviceFairMode, forward]);

  if (orderDataStore.clientSecretLoadingError) {
    return <ErrorPage message={t('errors:stripe_intent_error_message')} />;
  }

  return (
    <PageContainer>
      <Stack gap={'var(--mantine-spacing-lg)'} style={{ height: '100%' }}>
        <Stack gap={0}>
          <SimpleStepDots step={2} max={3} />
          <Title order={2}>{t('payment:payment_title')}</Title>
          <Text>
            {stripeLoaded ? t('payment:payment_message') : t('common:loading')}
          </Text>
        </Stack>

        {stripeLoaded && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: clientSecret, appearance }}
          >
            <CheckoutForm forward={forward} back={back} />
          </Elements>
        )}

        {!stripeLoaded && stripeApiKey && <LoadingPage icon={false} />}
        {!stripeApiKey && <div>NX_PUBLIC_STRIPE_API_KEY is missing</div>}
      </Stack>
    </PageContainer>
  );
});
